import { createContext, type ReactNode, useContext, useMemo, useState } from "react";


export type ContextualTip = {
  id: string;
  description: string;
  title?: string;
  variant?: "default" | "warning" | "destructive";
  Icon?: React.ComponentType<{ className?: string, size?: number }>;
}

type SetTipsParams = {
  // If true, the tips will only be added if there are no existing tips
  ifEmpty?: boolean
}


interface ContextualTipsContextType {
  tips: ContextualTip[];
  setTips: (tips: ContextualTip[], params?: SetTipsParams) => void;
  isHidden: boolean;
  setIsHidden: (hidden: boolean) => void;
}


const ContextualTipsContext = createContext<
  ContextualTipsContextType | undefined
>(undefined);


export interface ContextualTipsProviderProps {
  children: ReactNode;
}


const CONTEXTUAL_TIPS_HIDDEN_KEY = "contextual-tips-hidden";

export function ContextualTipsProvider({
  children,
}: ContextualTipsProviderProps) {
  const [tips, setTips] = useState<ContextualTip[]>([]);
  const [isHidden, setIsHiddenState] = useState<boolean>(() => {

    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(CONTEXTUAL_TIPS_HIDDEN_KEY);
      return stored === "true";
    }
    return false;
  });

  const handleSetTips = (newTips: ContextualTip[], params?: SetTipsParams) => {
    if (params?.ifEmpty && tips.length > 0) {
      return;
    }
    const hasChanged = newTips.length !== tips.length || newTips.some(tip => {
      const relatedTip = tips.find(t => t.id === tip.id);
      return (
        relatedTip?.title !== tip.title ||
        relatedTip?.description !== tip.description ||
        relatedTip?.variant !== tip.variant ||
        relatedTip?.Icon !== tip.Icon
      );
    });

    if (hasChanged) {
      setTips(newTips);
    }
  };

  const setIsHidden = (hidden: boolean) => {
    setIsHiddenState(hidden);
    if (typeof window !== "undefined") {
      localStorage.setItem(CONTEXTUAL_TIPS_HIDDEN_KEY, String(hidden));
    }
  };

  const value = useMemo(() => ({
    tips,
    setTips: handleSetTips,
    isHidden,
    setIsHidden
  }), [tips, handleSetTips, isHidden]);

  return (
    <ContextualTipsContext.Provider value={value}>
      {children}
    </ContextualTipsContext.Provider>
  );
}


export function useContextualTips() {
  const context = useContext(ContextualTipsContext);

  if (context === undefined) {
    throw new Error(
      "useContextualTips must be used within a ContextualTipsProvider",
    );
  }
  return context;
}
